import React, { useState, useEffect, useCallback } from 'react';
import {
    getTournaments, createTournament, deleteTournament,
    getTeams, createTeam, deleteTeam,
    getMatchesForTournament, createMatch, resolveMatch, deleteMatch, updateMatch,
    createEvent, resolveEvent, deleteEvent, toggleEventLock, toggleTournamentPickem, toggleTournamentSync
} from '../../services/api';
import TeamSearchInput from './TeamSearchInput';

const toLocalISOString = (date) => {
    const dt = new Date(date);
    // Получаем смещение в минутах и вычитаем его, чтобы "сдвинуть" время в локальное
    const tzoffset = dt.getTimezoneOffset() * 60000;
    const localISOTime = new Date(dt.getTime() - tzoffset).toISOString().slice(0, 16);
    return localISOTime;
};
const getTodayMidnight = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00`;
};

const calculateOppositeOdds = (value) => {
    const v = parseFloat(value).toFixed(2);

    const grid = {
        // Сверх-фавориты
        "1.01": 15.0, "1.02": 11.5, "1.03": 10.0, "1.04": 9.50, "1.05": 9.20,
        "1.06": 9.00, "1.07": 8.50, "1.08": 8.00, "1.09": 7.50, "1.10": 7.00,
        "1.11": 6.50, "1.12": 6.20, "1.13": 6.00, "1.14": 5.80, "1.15": 5.50,

        // Плотные фавориты
        "1.20": 4.50, "1.22": 4.20, "1.25": 3.90, "1.26": 3.40, "1.28": 3.35,
        "1.30": 3.30, "1.32": 3.25, "1.35": 3.20, "1.38": 3.05, "1.40": 3.00,
        "1.42": 2.90, "1.45": 2.80, "1.48": 2.70, "1.50": 2.60, "1.55": 2.45,

        // Близкие матчи (Равные шансы)
        "1.60": 2.30, "1.62": 2.25, "1.65": 2.10, "1.70": 2.05, "1.72": 2.00,
        "1.75": 1.95, "1.80": 1.90, "1.82": 2.00, "1.85": 1.85, "1.90": 1.80,
        "2.00": 1.82, "2.10": 1.65, "2.20": 1.60, "2.25": 1.58, "2.30": 1.55,

        // Аутсайдеры (обратные значения)
        "2.50": 1.50, "2.70": 1.48, "2.80": 1.45, "2.90": 1.42, "3.00": 1.40,
        "3.20": 1.35, "3.40": 1.26, "4.00": 1.22, "5.00": 1.18, "6.00": 1.13,
        "7.00": 1.10, "8.00": 1.08, "9.00": 1.06, "10.0": 1.03, "12.0": 1.02,
        "15.0": 1.01
    };

    // 1. Прямое попадание в сетку
    if (grid[v]) return grid[v];

    // 2. Если значения нет в сетке, ищем ближайшее число в ключевых точках
    const keys = Object.keys(grid).map(Number).sort((a, b) => a - b);
    if (parseFloat(v) > keys[keys.length - 1]) return 1.01;
    if (parseFloat(v) < keys[0]) return 15.0;

    // 3. Математический фоллбэк (если кэф специфический, например 1.37)
    // Используем среднюю маржу 5.26%
    const margin = 1.0526;
    const calculated = 1 / (margin - (1 / parseFloat(v)));

    // Округляем до "красивого" букмекерского шага
    return smartRound(calculated);
};

/**
 * Вспомогательная функция для округления в стиле БК
 */
function smartRound(num) {
    if (num > 5) return Math.round(num * 2) / 2; // Шаг 0.5 для больших кэфов
    if (num > 3) return Math.round(num * 10) / 10; // Шаг 0.1
    return Math.round(num * 100) / 100; // Шаг 0.01
}

function MatchEditor({ match, onUpdate, onResolve, onDelete }) {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState({
        startTime: toLocalISOString(match.startTime),
        odds1: match.odds1,
        odds2: match.odds2,
        odds3: match.odds3,
        matchUrl: match.matchUrl || '',
    });
    const [showEvents, setShowEvents] = useState(false);
    const [newEvent, setNewEvent] = useState({ name: '', option1: '', option2: '', option3: '', odds1: '', odds2: '', odds3: '' });

    const handleUpdate = async () => {
        try {
            await updateMatch(match.id, data);
            setIsEditing(false);
            onUpdate(); // Обновляем весь список
        } catch (error) {
            alert(error.response?.data?.error || 'Ошибка обновления');
        }
    };

    const handleChange = (e) => {
        setData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };
    const handleAddEvent = async () => {
        if (!newEvent.name || !newEvent.option1 || !newEvent.option2 || !newEvent.odds1 || !newEvent.odds2) {
            alert('Заполните все поля события');
            return;
        }
        if (newEvent.option3 && !newEvent.odds3) {
            alert('Укажите коэффициент для 3-й опции');
            return;
        }
        try {
            await createEvent({
                matchId: match.id,
                name: newEvent.name,
                option1: newEvent.option1,
                option2: newEvent.option2,
                odds1: newEvent.odds1,
                odds2: newEvent.odds2,
                option3: newEvent.option3 || undefined,
                odds3: newEvent.odds3 || undefined
            });
            setNewEvent({ name: '', option1: '', option2: '', option3: '', odds1: '', odds2: '', odds3: '' });
            onUpdate(); // Обновляем данные матча, чтобы увидеть новое событие
        } catch (e) {
            alert(e.response?.data?.error || 'Ошибка создания события');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (window.confirm('Удалить событие? Ставки вернутся.')) {
            try {
                await deleteEvent(eventId);
                onUpdate();
            } catch (e) { alert('Ошибка удаления'); }
        }
    };

    const handleResolveEvent = async (eventId, winnerChoice) => {
        // winnerChoice: 1 или 2
        if (window.confirm(`Победил вариант ${winnerChoice}?`)) {
            try {
                await resolveEvent(eventId, winnerChoice);
                onUpdate();
            } catch (e) { alert('Ошибка расчета'); }
        }
    };
    const handleToggleEventLock = async (eventId) => {
        try {
            await toggleEventLock(eventId);
            onUpdate(); // Обновляем список, чтобы увидеть изменение статуса
        } catch (e) {
            alert('Ошибка изменения статуса');
        }
    };

    if (isEditing) {
        return (
            <li className="admin-list-item editing">
                <input type="datetime-local" name="startTime" value={data.startTime} onChange={handleChange} />
                <input type="number" step="0.01" name="odds1" value={data.odds1} onChange={handleChange} />
                <input type="number" step="0.01" name="odds2" value={data.odds2} onChange={handleChange} />
                {match.team3Id && (<input type="number" step="0.01" name="odds3" value={data.odds3} onChange={handleChange} />)}
                <input type="text" name="matchUrl" value={data.matchUrl} onChange={handleChange} placeholder="URL матча" />
                <button onClick={handleUpdate}>✓</button>
                <button onClick={() => setIsEditing(false)}>✗</button>
            </li>
        );
    }

    return (
        <li className="admin-list-item">
            <span>{match.team1.name} vs {match.team2.name}{match.team3Id && (<> vs {match.team3.name}</>)}</span>
            <div className="match-actions">
                {match.winnerId ? (
                    <span className="match-status">Завершен</span>
                ) : (
                    <>
                        <button onClick={() => setIsEditing(true)}>✎ Ред.</button>
                        <button onClick={() => setShowEvents(!showEvents)} className="secondary-btn xs">
                            {showEvents ? 'Скрыть события' : `События (${match.events?.length || 0})`}
                        </button>
                        <button onClick={() => onResolve(match.id, match.team1Id)} style={{ backgroundColor: '#f05858' }} title={`Назначить победителем ${match.team1.name}`}>
                            Поб. {match.team1.name}
                        </button>
                        <button onClick={() => onResolve(match.id, match.team2Id)} style={{ backgroundColor: '#f05858' }} title={`Назначить победителем ${match.team2.name}`}>
                            Поб. {match.team2.name}
                        </button>
                        {match.team3Id && (
                            <button onClick={() => onResolve(match.id, match.team3Id)} style={{ backgroundColor: '#f05858' }} title={`Назначить победителем ${match.team3.name}`}>
                                Поб. {match.team3.name}
                            </button>
                        )}
                    </>
                )}
                <button onClick={() => onDelete(match.id)} title="Удалить матч">
                    &times;
                </button>
            </div>
            {showEvents && (
                <div className="admin-events-container-web">

                    {/* Форма создания события */}
                    <div className="event-create-web">
                        <input
                            className="event-name-input"
                            placeholder="Название события (напр. Победитель 1 карты)"
                            value={newEvent.name}
                            onChange={e => setNewEvent({ ...newEvent, name: e.target.value })}
                        />

                        <div className="event-inputs-row">
                            <div className="input-group">
                                <input placeholder="Опция 1" value={newEvent.option1} onChange={e => setNewEvent({ ...newEvent, option1: e.target.value })} />
                                <input type="number" step="0.01" placeholder="Кф" className="odds-input" value={newEvent.odds1} onChange={e => setNewEvent({ ...newEvent, odds1: e.target.value })} />
                            </div>

                            <span className="vs-separator">vs</span>

                            <div className="input-group">
                                <input placeholder="Опция 2" value={newEvent.option2} onChange={e => setNewEvent({ ...newEvent, option2: e.target.value })} />
                                <input type="number" step="0.01" placeholder="Кф" className="odds-input" value={newEvent.odds2} onChange={e => setNewEvent({ ...newEvent, odds2: e.target.value })} />
                            </div>

                            <span className="vs-separator">vs</span>

                            <div className="input-group">
                                <input placeholder="Опция 3 (Опц.)" value={newEvent.option3 || ''} onChange={e => setNewEvent({ ...newEvent, option3: e.target.value })} />
                                <input type="number" placeholder="Кф" className="odds-input" value={newEvent.odds3 || ''} onChange={e => setNewEvent({ ...newEvent, odds3: e.target.value })} />
                            </div>

                            <button onClick={handleAddEvent} className="add-event-btn">Добавить</button>
                        </div>
                    </div>

                    {/* Список существующих событий */}
                    <div className="events-list-web">
                        {match.events?.length === 0 && <p className="no-events-text">Нет дополнительных событий</p>}

                        {match.events?.map(ev => (
                            <div key={ev.id} className="admin-event-row">
                                <div className="event-info-col">
                                    <span className="event-name">
                                        {ev.name}
                                        {/* Показываем статус, если заблокировано */}
                                        {ev.isLocked && !ev.winner && <span style={{ color: '#f04747', marginLeft: 8 }}>(LOCKED)</span>}
                                    </span>
                                    <span className="event-meta">
                                        {ev.option1} <strong>({ev.odds1})</strong> — {ev.option2} <strong>({ev.odds2})</strong>
                                        {ev.option3 && <> — {ev.option3} <strong>({ev.odds3})</strong> </>}
                                    </span>
                                </div>

                                <div className="event-actions-col">
                                    {!ev.winner ? (
                                        <>
                                            <button
                                                className="lock-icon-btn"
                                                onClick={() => handleToggleEventLock(ev.id)}
                                                title={ev.isLocked ? "Открыть ставки" : "Закрыть ставки"}
                                            >
                                                {ev.isLocked ? '🔓' : '🔒'}
                                            </button>
                                            <button className="resolve-btn" onClick={() => handleResolveEvent(ev.id, 1)}>
                                                Win {ev.option1}
                                            </button>
                                            <button className="resolve-btn" onClick={() => handleResolveEvent(ev.id, 2)}>
                                                Win {ev.option2}
                                            </button>
                                            {ev.option3 && (
                                                <button className="resolve-btn" onClick={() => handleResolveEvent(ev.id, 3)}>
                                                    Win {ev.option3}
                                                </button>
                                            )}
                                            <button className="delete-icon-btn" onClick={() => handleDeleteEvent(ev.id)} title="Удалить событие">
                                                &times;
                                            </button>
                                        </>
                                    ) : (
                                        <span className="winner-label">
                                            Победитель: <strong>{ev.winner === 1 ? ev.option1 : ev.option2}</strong>
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </li>
    );
}

// --- КОМПОНЕНТ ДЛЯ ВКЛАДКИ "ТУРНИРЫ" ---
function TournamentsTab({ tournaments, refreshData }) {
    const [name, setName] = useState('');
    const [pageUrl, setPageUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [hasPickem, setHasPickem] = useState(false);

    const handleTogglePickem = async (id) => {
        try {
            await toggleTournamentPickem(id);
            refreshData(); // Обновляем список, чтобы увидеть изменения
        } catch (e) {
            alert('Ошибка переключения статуса Пикема');
        }
    };

    const handleToggleSync = async (id) => {
        try {
            await toggleTournamentSync(id);
            refreshData(); // Обновляем список, чтобы увидеть изменения
        } catch (e) {
            alert('Ошибка переключения статуса синхронизации');
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        await createTournament({ name, pageUrl, logoUrl, startDate, endDate, hasPickem });
        setName(''); setPageUrl(''); setLogoUrl(''); setStartDate(''); setEndDate(''); setHasPickem(false);
        refreshData();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Вы уверены, что хотите удалить этот турнир? Все его матчи и ставки будут безвозвратно удалены!')) {
            await deleteTournament(id);
            refreshData();
        }
    };

    return (
        <div className="admin-section">
            <h4>Создать турнир</h4>
            <form onSubmit={handleCreate} className="admin-form grid-form">
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Название турнира" required className="full-width" />
                <input type="text" value={pageUrl} onChange={e => setPageUrl(e.target.value)} placeholder="URL страницы турнира" />
                <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="URL логотипа" />
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />
                <button type="submit" className="full-width">Создать турнир</button>
            </form>

            <h4>Существующие турниры</h4>
            <ul className="admin-list">
                {tournaments.map(t => (
                    <li key={t.id}>
                        <div className="admin-tourney-info">
                            <span>{t.name}</span>
                            {/* Бейдж теперь работает как кнопка-переключатель */}
                            <button
                                className={`pickem-toggle-btn ${t.autoSync ? 'active' : ''}`}
                                onClick={() => handleToggleSync(t.id)}
                                title={t.autoSync ? "Выключить синхронизацию" : "Включить синхронизацию"}
                            >
                                {t.autoSync ? "SYNC: ON" : "SYNC: OFF"}
                            </button>
                            <button
                                className={`pickem-toggle-btn ${t.hasPickem ? 'active' : ''}`}
                                onClick={() => handleTogglePickem(t.id)}
                                title={t.hasPickem ? "Выключить Пикем" : "Включить Пикем"}
                            >
                                {t.hasPickem ? "PICK'EM: ON" : "PICK'EM: OFF"}
                            </button>
                        </div>
                        <button className="delete-button-small" onClick={() => handleDelete(t.id)}>&times;</button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// --- КОМПОНЕНТ ДЛЯ ВКЛАДКИ "КОМАНДЫ" ---
function TeamsTab({ teams, refreshData }) {
    const [name, setName] = useState('');
    const [profileUrl, setProfileUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [teamToDelete, setTeamToDelete] = useState(null);

    const handleCreate = async (e) => {
        e.preventDefault();
        await createTeam({ name, profileUrl, logoUrl });
        setName(''); setProfileUrl(''); setLogoUrl('');
        refreshData();
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        if (teamToDelete && window.confirm(`Удалить команду "${teamToDelete.name}"? Это невозможно, если она участвует в матчах.`)) {
            try {
                await deleteTeam(teamToDelete.id);
                setTeamToDelete(null);
                refreshData();
            } catch (error) {
                alert(error.response?.data?.error || 'Ошибка удаления');
            }
        }
    };

    return (
        <div className="admin-section">
            <h4>Добавить команду</h4>
            <form onSubmit={handleCreate} className="admin-form grid-form">
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Название команды" required className="full-width" />
                <input type="text" value={profileUrl} onChange={e => setProfileUrl(e.target.value)} placeholder="URL профиля команды" />
                <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="URL логотипа" />
                <button type="submit" className="full-width">Добавить команду</button>
            </form>

            <h4>Удалить команду</h4>
            <form onSubmit={handleDelete} className="admin-form">
                <TeamSearchInput teams={teams} onTeamSelect={setTeamToDelete} />
                <button type="submit" disabled={!teamToDelete} className="danger">Удалить выбранную команду</button>
            </form>
        </div>
    );
}

// --- КОМПОНЕНТ ДЛЯ ВКЛАДКИ "МАТЧИ" ---
function MatchesTab({ tournaments, teams, refreshData }) {
    const [selectedTournamentId, setSelectedTournamentId] = useState('');
    const [matches, setMatches] = useState([]);
    const [team1, setTeam1] = useState(null);
    const [team2, setTeam2] = useState(null);
    const [team3, setTeam3] = useState(null);
    const [odds1, setOdds1] = useState('');
    const [odds2, setOdds2] = useState('');
    const [odds3, setOdds3] = useState('');
    const [startTime, setStartTime] = useState(getTodayMidnight());
    const [matchUrl, setMatchUrl] = useState('');

    const refreshMatches = useCallback(() => {
        if (selectedTournamentId) {
            getMatchesForTournament(selectedTournamentId).then(res => setMatches(res.data));
        }
    }, [selectedTournamentId]);

    useEffect(refreshMatches, [refreshMatches]);

    const handleOddsChange = (e, field) => {
        const val = e.target.value;

        if (field === 'odds1') {
            setOdds1(val);
            // Пересчитываем только П2 и только если мы ввели значение в П1
            if (val) setOdds2(calculateOppositeOdds(val));
        } else {
            setOdds2(val);
            // Если правим П2 руками, П1 НЕ ТРОГАЕМ (убираем авторасчет в обратную сторону)
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        await createMatch({ tournamentId: selectedTournamentId, team1Id: team1.id, team2Id: team2.id, team3Id: team3 ? team3.id : null, odds1, odds2, odds3: odds3 || null, startTime, matchUrl });
        setTeam1(null); setTeam2(null); setTeam3(null); setOdds1(''); setOdds2(''); setOdds3(''); setMatchUrl('');
        getMatchesForTournament(selectedTournamentId).then(res => setMatches(res.data));
    };
    const handleDelete = async (matchId) => {
        if (window.confirm('Вы уверены, что хотите удалить этот матч? Все ставки на него будут удалены.')) {
            await deleteMatch(matchId);
            refreshMatches(); // Обновляем список матчей
        }
    };

    const handleResolve = async (matchId, winnerId) => {
        await resolveMatch(matchId, winnerId);
        refreshMatches();
    };
    const [finishedMatches, activeMatches] = matches.reduce(
        (acc, match) => {
            if (match.winnerId) {
                acc[0].push(match); // Чётные числа
            } else {
                acc[1].push(match); // Нечётные числа
            }
            return acc;
        },
        [[], []]
    );
    return (
        <div className="admin-section">
            <select style={{ width: '100%' }} value={selectedTournamentId} onChange={e => setSelectedTournamentId(e.target.value)}>
                <option value="" disabled>Сначала выберите турнир</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            {selectedTournamentId && (
                <>
                    <h4>Добавить матч в турнир</h4>
                    <form onSubmit={handleCreate} className="admin-form">
                        <div className="grid-form-row"> {/* Предполагаем, что у вас есть стили для ряда */}
                            {/* Команда 1 */}
                            <div>
                                <TeamSearchInput teams={teams} onTeamSelect={setTeam1} placeholder="Команда 1" value={team1} />
                                <input type="number" step="0.01" value={odds1} onChange={(e) => handleOddsChange(e, 'odds1')} placeholder="Кф 1" />
                            </div>

                            {/* Команда 2 */}
                            <div>
                                <TeamSearchInput teams={teams} onTeamSelect={setTeam2} placeholder="Команда 2" value={team2} />
                                <input type="number" step="0.01" value={odds2} onChange={(e) => handleOddsChange(e, 'odds2')} placeholder="Кф 2" />
                            </div>

                            {/* Команда 3 (Опционально) */}
                            <div>
                                <TeamSearchInput teams={teams} onTeamSelect={setTeam3} placeholder="Команда 3 (Опц.)" value={team3} />
                                <input type="number" step="0.01" value={odds3} onChange={e => setOdds3(e.target.value)} placeholder="Кф 3" />
                            </div>
                        </div>
                        <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} required className="full-width" />
                        <input type="text" value={matchUrl} onChange={e => setMatchUrl(e.target.value)} placeholder="URL страницы матча (опционально)" className="full-width" />
                        <button type="submit" disabled={!team1 || !team2} className="full-width">Создать матч</button>
                    </form>
                    {activeMatches.length > 0 && (
                        <>
                            <h4>Активные матчи:</h4>
                            <ul className="admin-list matches">
                                {activeMatches.map(m => (
                                    <MatchEditor
                                        key={m.id}
                                        match={m}
                                        onUpdate={refreshMatches}
                                        // --- ПЕРЕДАЕМ НОВЫЕ ОБРАБОТЧИКИ ---
                                        onResolve={handleResolve}
                                        onDelete={handleDelete}
                                    />
                                ))}
                            </ul>
                        </>
                    )}

                    {finishedMatches.length > 0 && (
                        <>
                            <h4>Завершенные матчи:</h4>
                            <ul className="admin-list matches">
                                {finishedMatches.reverse().map(m => (
                                    <MatchEditor
                                        key={m.id}
                                        match={m}
                                        onUpdate={refreshMatches}
                                        // --- ПЕРЕДАЕМ НОВЫЕ ОБРАБОТЧИКИ ---
                                        onResolve={handleResolve}
                                        onDelete={handleDelete}
                                    />
                                ))}
                            </ul>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

// --- ОСНОВНОЙ КОМПОНЕНТ-КОНТЕЙНЕР ---
export default function BettingAdminPanel() {
    const [activeTab, setActiveTab] = useState('matches');
    const [tournaments, setTournaments] = useState([]);
    const [teams, setTeams] = useState([]);

    const fetchData = useCallback(() => {
        getTournaments().then(res => setTournaments(res.data));
        getTeams().then(res => setTeams(res.data));
    }, []);

    useEffect(fetchData, [fetchData]);

    return (
        <div className="betting-admin-panel">
            <div className="admin-tabs">
                <button className={activeTab === 'matches' ? 'active' : ''} onClick={() => setActiveTab('matches')}>Матчи</button>
                <button className={activeTab === 'tournaments' ? 'active' : ''} onClick={() => setActiveTab('tournaments')}>Турниры</button>
                <button className={activeTab === 'teams' ? 'active' : ''} onClick={() => setActiveTab('teams')}>Команды</button>
            </div>
            <div className="admin-tab-content">
                {activeTab === 'matches' && <MatchesTab tournaments={tournaments} teams={teams} refreshData={fetchData} />}
                {activeTab === 'tournaments' && <TournamentsTab tournaments={tournaments} refreshData={fetchData} />}
                {activeTab === 'teams' && <TeamsTab teams={teams} refreshData={fetchData} />}
            </div>
        </div>
    );
}